import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import confetti from "canvas-confetti";
import { play } from "../../lib/sound";
import { toast } from "sonner";
import { ArrowLeft, Gift, Map, Utensils, Heart, Star, Trophy, Gem, Award, Medal, Lock, CheckCircle2, Target } from "lucide-react";

const ICONS = { gift: Gift, map: Map, utensils: Utensils, heart: Heart, star: Star, target: Target };
const TIER_COLORS = { bronze: "#CD7F32", silver: "#C7CAD1", gold: "#FFD700", platinum: "#7DF9FF" };
const LEVEL_ICONS = { bronze: Medal, silver: Award, gold: Trophy, platinum: Gem };

function Card({ m }) {
  const Icon = ICONS[m.icon] || Target;
  const pct = Math.min(100, Math.round((m.current_value / m.max_threshold) * 100));
  const col = m.tier_reached ? TIER_COLORS[m.tier_reached] : "#00E676";
  return (
    <div data-testid={`user-mission-${m.code}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }}><Icon className="h-5 w-5" style={{ color: col }} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><h4 className="font-semibold">{m.title}</h4>{m.completed && <CheckCircle2 className="h-4 w-4 text-[#00E676]" />}</div>
          <p className="text-xs text-zinc-500 mt-0.5">{m.description}</p>
        </div>
      </div>
      <div className="mb-2 flex items-center justify-between text-xs"><span className="text-zinc-400">Progresso: <b className="text-white" data-testid={`user-mission-value-${m.code}`}>{m.current_value}</b> / {m.max_threshold}</span>{m.tier_reached && <span className="uppercase font-semibold" style={{ color: col }}>{m.tier_reached}</span>}</div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${col}, #00E676)` }} /></div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {m.tiers.map((t) => { const done = m.current_value >= t.threshold; const c = TIER_COLORS[t.key]; return (
          <div key={t.key} className="text-[10px] px-2 py-1 rounded-md flex items-center gap-1" style={{ background: done ? `${c}22` : "rgba(255,255,255,0.03)", border: `1px solid ${done ? c + "66" : "rgba(255,255,255,0.08)"}`, color: done ? c : "#a1a1aa" }}>
            {done ? <CheckCircle2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}<span>{t.label} · {t.threshold}</span><span className="opacity-70">+{t.reward.credits}c</span>
          </div>); })}
      </div>
    </div>
  );
}

export default function UserMissions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const loadMissions = () => { setErr(false); api.get("/user/missions").then(({ data }) => setData(data || {})).catch(() => setErr(true)); };
  useEffect(() => {
    loadMissions();
    api.get("/user/rewards/unseen").then(({ data }) => {
      if (data && data.length) {
        confetti({ particleCount: 130, spread: 80, origin: { y: 0.35 }, colors: ["#00E676", "#FFD700", "#7DF9FF"] });
        play("mission_completed");
        if (data.some((r) => (r.reward?.credits || 0) > 0)) setTimeout(() => play("credits"), 450);
        if (data.some((r) => r.reward?.badge)) setTimeout(() => play("badge"), 800);
        data.slice(0, 3).forEach((r) => toast.success(`Missione «${r.mission_title}» — ${r.tier_label}! +${r.reward?.credits || 0} crediti`, { duration: 7000 }));
        api.post("/user/rewards/seen").catch(() => {});
      }
    }).catch(() => {});
  }, []);
  if (err) return (
    <div className="min-h-screen bg-[#0A0A0C] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="missions-error">
      <div className="text-zinc-400">Non siamo riusciti a caricare le tue missioni.</div>
      <button onClick={loadMissions} data-testid="missions-retry" className="px-5 py-2.5 rounded-xl bg-[#00E676] text-black font-semibold active:scale-95 transition-transform">Riprova</button>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center text-zinc-500" data-testid="missions-loading">Caricamento…</div>;
  if (!data.level) return (
    <div className="min-h-screen bg-[#0A0A0C] flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="missions-empty">
      <div className="text-zinc-300 font-semibold">Nessuna missione al momento</div>
      <div className="text-zinc-500 text-sm">Torna presto: nuove missioni per far crescere Fuma nel tuo comune sono in arrivo.</div>
    </div>
  );
  const lv = data.level;
  const LvIcon = LEVEL_ICONS[lv.level] || Trophy;
  const c = TIER_COLORS[lv.level] || "#00E676";
  const span = lv.next_level_xp != null ? lv.next_level_xp - lv.current_level_xp : 1;
  const xpPct = lv.next_level_xp != null ? Math.min(100, Math.round(((lv.xp - lv.current_level_xp) / span) * 100)) : 100;
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="user-missions-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/esplora")} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 text-sm" data-testid="missions-back"><ArrowLeft className="h-4 w-4" /> Mappa</button>
        <div data-testid="user-level-card" className="relative overflow-hidden rounded-2xl p-6 border border-white/10 mb-8" style={{ background: `radial-gradient(120% 140% at 0% 0%, ${c}22, #131316 60%)` }}>
          <div className="flex items-center gap-5 flex-wrap">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ background: `${c}22`, border: `1px solid ${c}66`, boxShadow: `0 0 30px ${c}33` }}><LvIcon className="h-8 w-8" style={{ color: c }} /></div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs uppercase tracking-widest text-zinc-400">Il tuo livello</div>
              <div className="text-2xl font-display font-bold" style={{ color: c }} data-testid="user-level-label">{lv.level_label}</div>
              {lv.next_level ? (<div className="mt-2"><div className="h-2 rounded-full bg-white/10 overflow-hidden max-w-sm"><div className="h-full rounded-full" style={{ width: `${xpPct}%`, background: `linear-gradient(90deg, ${c}, #00E676)` }} /></div><div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · prossimo {lv.next_level} a {lv.next_level_xp} XP</div></div>) : <div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · livello massimo</div>}
            </div>
            <div className="text-center px-4 py-2 rounded-xl bg-white/5 border border-white/10"><div className="flex items-center gap-1 text-[#00E676] font-bold text-xl" data-testid="user-credits"><Gem className="h-4 w-4" /> {lv.credits}</div><div className="text-[10px] uppercase tracking-wide text-zinc-500">Crediti</div></div>
          </div>
          <p className="text-xs text-zinc-400 mt-3">100 crediti = 1€ di sconto al prossimo ordine.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{data.missions.map((m) => <Card key={m.code} m={m} />)}</div>
      </div>
    </div>
  );
}
